import type { CanonicalError, CanonicalStage } from "../api/contracts";

export type ConnectionState = "connected" | "connecting" | "reconnecting";

export interface VisibleStatus {
  stage: CanonicalStage;
  reasonCode: string | null;
  message: string | null;
}

export function CompactStatus({
  connection,
  error,
  status,
}: {
  connection: ConnectionState;
  error: CanonicalError | null;
  status: VisibleStatus | null;
}) {
  if (error !== null) {
    return (
      <div className="compact-status compact-status--error" role="alert">
        <span className="status-dot" aria-hidden="true" />
        <span>{error.message}</span>
        <code>{error.code}</code>
      </div>
    );
  }

  if (connection !== "connected") {
    return (
      <div className="compact-status compact-status--pending" role="status">
        <span className="status-dot" aria-hidden="true" />
        <span>{connection === "reconnecting" ? "Reconnecting…" : "Connecting…"}</span>
      </div>
    );
  }

  if (status === null) {
    return (
      <div className="compact-status compact-status--neutral" role="status">
        <span className="status-dot" aria-hidden="true" />
        <span>Ready</span>
      </div>
    );
  }

  const isError = status.stage === "blocked" || status.stage === "failed";
  return (
    <div
      className={`compact-status ${isError ? "compact-status--error" : `compact-status--${status.stage}`}`}
      role={isError ? "alert" : "status"}
    >
      <span className="status-dot" aria-hidden="true" />
      <span>{status.message ?? labelForStage(status.stage)}</span>
      {status.reasonCode ? <code>{status.reasonCode}</code> : null}
    </div>
  );
}

function labelForStage(stage: CanonicalStage): string {
  switch (stage) {
    case "accepted":
      return "Accepted";
    case "platform_processing":
      return "Platform processing";
    case "delivered":
      return "Delivered";
    case "blocked":
      return "Blocked";
    case "failed":
      return "Failed";
  }
}
